import { type AppState, Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react"

type AuthMode = "auth0" | "mock"

type AuthConfigResponse = {
  mode: AuthMode
  auth0: {
    domain: string
    clientId: string
    audience: string | null
    redirectPath: string
  }
}

type AuthRuntime = {
  mode: AuthMode
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  login: () => Promise<void>
  logout: () => void
  getAccessToken: () => Promise<string | null>
}

const AuthRuntimeContext = createContext<AuthRuntime | null>(null)

const mockRuntime: AuthRuntime = {
  mode: "mock",
  isLoading: false,
  isAuthenticated: true,
  error: null,
  login: async () => {},
  logout: () => {},
  getAccessToken: async () => null
}

function normalizeRedirectPath(rawPath: string): string {
  const trimmed = rawPath.trim()
  if (!trimmed) {
    return "/auth/callback"
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`
}

function onRedirectCallback(appState?: AppState) {
  const returnTo =
    appState && typeof appState === "object" && "returnTo" in appState && typeof appState.returnTo === "string"
      ? appState.returnTo
      : "/"
  window.location.replace(returnTo)
}

function Auth0RuntimeProvider({ audience, children }: { audience: string | null; children: ReactNode }) {
  const { isLoading, isAuthenticated, error, loginWithRedirect, logout, getIdTokenClaims, getAccessTokenSilently } =
    useAuth0()

  const runtime = useMemo<AuthRuntime>(
    () => ({
      mode: "auth0",
      isLoading,
      isAuthenticated,
      error: error?.message ?? null,
      login: async () => {
        await loginWithRedirect({
          appState: {
            returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`
          }
        })
      },
      logout: () => {
        logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        })
      },
      getAccessToken: async () => {
        if (!isAuthenticated) {
          return null
        }

        if (audience) {
          try {
            return await getAccessTokenSilently({
              authorizationParams: {
                audience
              }
            })
          } catch {
            // Fall back to ID token when SPA API audience is not configured.
          }
        }

        try {
          const claims = (await getIdTokenClaims()) as { __raw?: string } | undefined
          return claims?.__raw ?? null
        } catch {
          return null
        }
      }
    }),
    [audience, error, getAccessTokenSilently, getIdTokenClaims, isAuthenticated, isLoading, loginWithRedirect, logout]
  )

  return <AuthRuntimeContext.Provider value={runtime}>{children}</AuthRuntimeContext.Provider>
}

function MockRuntimeProvider({ children }: { children: ReactNode }) {
  return <AuthRuntimeContext.Provider value={mockRuntime}>{children}</AuthRuntimeContext.Provider>
}

// Eagerly fetch auth config at module load — avoids fetch-in-useEffect.
const authConfigPromise: Promise<AuthConfigResponse> = fetch("/api/auth/config").then(async (response) => {
  if (!response.ok) {
    throw new Error(`Failed to load auth config (HTTP ${response.status})`)
  }
  return (await response.json()) as AuthConfigResponse
})

function useAuthConfig() {
  const [config, setConfig] = useState<AuthConfigResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    authConfigPromise
      .then((loaded) => {
        if (!cancelled) setConfig(loaded)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load auth config")
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { config, error }
}

export function AuthRuntimeProvider({ children }: { children: ReactNode }) {
  const { config, error } = useAuthConfig()

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">Initializing authentication...</p>
      </div>
    )
  }

  const domain = config.auth0.domain.trim()
  const clientId = config.auth0.clientId.trim()
  const audience = config.auth0.audience?.trim() || null

  if (config.mode === "mock") {
    return <MockRuntimeProvider>{children}</MockRuntimeProvider>
  }

  if (!(domain && clientId)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-sm text-destructive">AUTH0_DOMAIN and AUTH0_CLIENT_ID must be configured for auth0 mode.</p>
      </div>
    )
  }

  const redirectPath = normalizeRedirectPath(config.auth0.redirectPath)

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}${redirectPath}`,
        ...(audience ? { audience } : {})
      }}
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0RuntimeProvider audience={audience}>{children}</Auth0RuntimeProvider>
    </Auth0Provider>
  )
}

export function useAuthRuntime(): AuthRuntime {
  const ctx = useContext(AuthRuntimeContext)
  if (!ctx) {
    throw new Error("useAuthRuntime must be used within AuthRuntimeProvider")
  }

  return ctx
}
